import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import ContactModal from "@/components/ContactModal";
import { VENDOR_PRODUCT_OPTIONS } from "@/lib/productOptions";
import { buildLoginPath, clearLoginRedirect, EXPLICIT_LOGOUT_KEY } from "@/lib/loginRedirect";
import { Menu, X, ShoppingCart, UserCircle } from "lucide-react";
import { Analytics } from "@/lib/analyticsShim";

const RAILAXIS_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/assets/Logo/Railaxis_logo-wbg.png`;

function getVendorProductPath(productSlug) {
  const rawSlug = String(productSlug || "").trim();
  if (!rawSlug) return null;
  const normalizedSlug = rawSlug.toLowerCase() === "di-pipes" ? "pvc-pipes" : rawSlug;
  const option = VENDOR_PRODUCT_OPTIONS.find(
    (item) => item.slug.toLowerCase() === normalizedSlug.toLowerCase()
  );
  return `/products/${option?.routeSlug || normalizedSlug}`;
}

export default function Navbar() {
  const { user, logout, loading: authLoading } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [contactOpen, setContactOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const isVendor = user?.role === "vendor";

  const scrollOrNavigate = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
    setMobileOpen(false);
  };

  const currentRedirectPath = () => {
    const current = `${location.pathname}${location.search}${location.hash}`;
    if (!current || current === "/" || current.startsWith("/login")) return "/";
    return current;
  };

  const openLogin = () => {
    sessionStorage.removeItem(EXPLICIT_LOGOUT_KEY);
    navigate(buildLoginPath(currentRedirectPath()));
    setMobileOpen(false);
  };

  const handleLogout = async () => {
    sessionStorage.setItem(EXPLICIT_LOGOUT_KEY, "1");
    clearLoginRedirect();
    await logout();
    setMobileOpen(false);
    navigate("/", { replace: true });
  };

  const openProfile = () => {
    navigate("/profile");
    setMobileOpen(false);
  };

  const openProducts = () => {
    if (isVendor) {
      const vendorProductPath = getVendorProductPath(user?.vendor_product_slug);
      if (vendorProductPath) {
        navigate(vendorProductPath);
        setMobileOpen(false);
        return;
      }
    }

    scrollOrNavigate("categories");
  };

  const navLinks = (() => {
    if (authLoading || isAdmin) return [];
    if (isVendor) {
      return [
        { label: "Products", action: openProducts, testid: "nav-products" },
        { label: "Vendor Orders", action: () => { navigate("/vendor-orders"); setMobileOpen(false); }, testid: "nav-vendor-orders" },
        { label: "Contact", action: () => { setContactOpen(true); setMobileOpen(false); }, testid: "nav-contact" },
      ];
    }
    return [
      { label: "Products", action: openProducts, testid: "nav-products" },
      { label: "Orders", action: () => { navigate("/orders"); setMobileOpen(false); }, testid: "nav-orders" },
      { label: "About Us", action: () => scrollOrNavigate("about"), testid: "nav-about" },
      { label: "Contact", action: () => { setContactOpen(true); setMobileOpen(false); }, testid: "nav-contact" },
      { label: "Request a Quote", action: () => scrollOrNavigate("requirements"), testid: "nav-quote" },
    ];
  })();

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={isAdmin ? "/admin-dashboard" : "/"} className="flex items-center gap-2" data-testid="logo-link">
              <img
                src={RAILAXIS_LOGO_SRC}
                alt="RailAxis logo"
                className="w-9 h-9 rounded-md object-contain border border-gray-100 bg-white"
              />
              <div className="font-display font-bold text-xl text-[#111827] tracking-tight">
                Rail<span className="text-[#C8A96A]">Axis</span>
              </div>
            </Link>

            {/* Center Links */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((l) => (
                <button
                  key={l.label}
                  onClick={l.action}
                  data-testid={l.testid}
                  className="link-gold text-sm font-medium"
                >
                  {l.label}
                </button>
              ))}
            </nav>

            {/* Right */}
            <div className="hidden lg:flex items-center gap-4">
              {!isAdmin && !isVendor && (
                <button
                  onClick={() => navigate("/orders")}
                  className="relative link-gold"
                  data-testid="cart-icon"
                  aria-label="Cart"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {items.length > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#C8A96A] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                      {items.length}
                    </span>
                  )}
                </button>
              )}

              {user ? (
                <div className="flex items-center gap-3">
                  {isAdmin && (
                    <button
                      onClick={() => navigate("/admin-dashboard")}
                      className="text-sm link-gold"
                    >
                      Dashboard
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={openProfile}
                    className="w-9 h-9 rounded-full bg-[#C8A96A] text-white flex items-center justify-center font-semibold text-sm shadow-sm transition hover:bg-[#B09255] focus:outline-none focus:ring-2 focus:ring-[#C8A96A]/30"
                    data-testid="user-avatar"
                    aria-label="Open profile"
                    title="Profile"
                  >
                    {user.name?.charAt(0).toUpperCase() || user.company?.charAt(0).toUpperCase() || <UserCircle className="w-5 h-5" />}
                  </button>
                  <button onClick={handleLogout} className="text-sm link-gold" data-testid="logout-btn">Logout</button>
                </div>
              ) : (
                <button
                  onClick={openLogin}
                  data-testid="login-btn"
                  className="btn-gold text-sm font-semibold px-5 py-2 rounded-md"
                >
                  Login
                </button>
              )}
            </div>

            {/* Mobile actions */}
            <div className="lg:hidden flex items-center gap-2">
              {user && (
                <button
                  type="button"
                  onClick={openProfile}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-[#C8A96A] text-sm font-semibold text-white shadow-sm"
                  data-testid="user-avatar-mobile"
                  aria-label="Open profile"
                  title="Profile"
                >
                  {user.name?.charAt(0).toUpperCase() || user.company?.charAt(0).toUpperCase() || <UserCircle className="w-5 h-5" />}
                </button>
              )}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="p-2"
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {mobileOpen && (
            <div className="lg:hidden border-t border-gray-100 py-4 space-y-3">
              {navLinks.map((l) => (
                <button
                  key={l.label}
                  onClick={l.action}
                  className="block w-full text-left px-2 py-2 link-gold text-sm font-medium"
                  data-testid={`${l.testid}-mobile`}
                >
                  {l.label}
                </button>
              ))}
              <div className="border-t border-gray-100 pt-3">
                {user ? (
                  <div className="space-y-2">
                    {isAdmin && (
                      <button
                        onClick={() => { navigate("/admin-dashboard"); setMobileOpen(false); }}
                        className="bg-white border border-gray-300 px-5 py-2 rounded-md text-sm w-full text-[#111827]"
                      >
                        Dashboard
                      </button>
                    )}
                    <button
                      onClick={openProfile}
                      className="bg-white border border-gray-300 px-5 py-2 rounded-md text-sm w-full text-[#111827]"
                      data-testid="profile-btn-mobile"
                    >
                      Profile
                    </button>
                    <button onClick={handleLogout} className="btn-gold px-5 py-2 rounded-md text-sm w-full">Logout</button>
                  </div>
                ) : (
                  <button onClick={openLogin} className="btn-gold px-5 py-2 rounded-md text-sm w-full">Login</button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </>
  );
}
